import { useState } from "react";
import { Mail, Send, Globe } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/api";
import { ASSO } from "@/constants";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="py-12 sm:py-16 lg:py-20" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">Contact</p>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="contact-title">
              Parlons-en
            </h1>
            <p className="text-base text-slate-600 mt-3 leading-relaxed">
              Une question sur l'association, une envie de devenir bénévole, une idée d'événement ?
              Écrivez-nous via ce formulaire ou directement par email.
            </p>
          </div>
          <div className="space-y-4">
            <a href={`mailto:${ASSO.email}`} className="flex items-center gap-4 group" data-testid="contact-email-link">
              <div className="h-11 w-11 rounded-xl bg-sky-100 flex items-center justify-center group-hover:bg-sky-200 transition-colors">
                <Mail className="h-5 w-5 text-sky-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">Email</p>
                <p className="text-sm text-slate-500">{ASSO.email}</p>
              </div>
            </a>
            <a href={ASSO.helloassoUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group" data-testid="contact-helloasso-link">
              <div className="h-11 w-11 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                <Globe className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">HelloAsso</p>
                <p className="text-sm text-slate-500">Adhésions et dons en ligne</p>
              </div>
            </a>
          </div>
        </div>

        <Card className="lg:col-span-3 border-slate-200" data-testid="contact-form-card">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Nom complet</Label>
                  <Input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Votre nom" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email</Label>
                  <Input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="vous@exemple.fr" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-subject">Sujet</Label>
                <Input id="contact-subject" data-testid="contact-subject-input" required value={form.subject} onChange={set("subject")} placeholder="Objet de votre message" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-message">Message</Label>
                <Textarea id="contact-message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")} placeholder="Votre message…" />
              </div>
              <Button type="submit" className="bg-sky-700 hover:bg-sky-800 w-full sm:w-auto" disabled={sending} data-testid="contact-submit-btn">
                <Send className="h-4 w-4 mr-2" />
                {sending ? "Envoi…" : "Envoyer le message"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
